import { useEffect, useRef, useState } from "react";
import { ChevronDown, Users as UsersIcon, Check, UserCircle2, Baby, Heart } from "lucide-react";

/**
 * Profile switcher — a tiny dropdown that lives in the patient dashboard
 * header. Shows "Myself" plus every approved dependent. Clicking a profile
 * updates the active profile at the parent (PatientDashboard) which
 * re-scopes all dashboard / scan / family actions.
 */
export function ProfileSwitcher({ parentUser, dependents, activeDependentId, onChange }) {
    const approved = (dependents || []).filter((d) => d.status === "approved");
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const activeDep = approved.find((d) => d.id === activeDependentId);
    const label = activeDep ? activeDep.name : (parentUser?.first_name || parentUser?.name || "Myself");
    const subLabel = activeDep ? `${activeDep.relationship} · age ${activeDep.age}` : "Account owner";

    const relIcon = (rel) => {
        if (rel === "child") return Baby;
        if (rel === "spouse") return Heart;
        return UserCircle2;
    };

    return (
        <div className="relative" ref={rootRef} data-testid="profile-switcher">
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-[var(--medx-orange)] shadow-sm"
                data-testid="profile-switcher-trigger"
            >
                <div className={`w-7 h-7 rounded-full grid place-items-center text-white text-xs font-bold overflow-hidden ${activeDep ? "bg-gradient-to-br from-emerald-500 to-emerald-700" : "bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)]"}`}>
                    {activeDep ? (activeDep.first_name || "D").slice(0, 1).toUpperCase() : (parentUser?.photo_url ? <img src={parentUser.photo_url} alt="" className="w-full h-full object-cover" /> : (parentUser?.first_name || parentUser?.name || "U").slice(0, 1).toUpperCase())}
                </div>
                <div className="text-left leading-tight">
                    <div className="text-[10px] font-mono tracking-wider text-gray-400 uppercase">Profile</div>
                    <div className="text-xs font-semibold text-gray-800 truncate max-w-[160px]">{label}</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-40" data-testid="profile-switcher-menu">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <div className="text-[10px] font-mono tracking-[0.22em] text-gray-400">SWITCH PROFILE</div>
                        <div className="text-xs text-gray-500 mt-0.5">Scans + consults happen under the selected profile.</div>
                    </div>
                    <ul className="max-h-80 overflow-auto">
                        <li>
                            <button
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-orange-50 ${!activeDependentId ? "bg-orange-50/40" : ""}`}
                                onClick={() => { onChange(null); setOpen(false); }}
                                data-testid="profile-switcher-self"
                            >
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-xs font-bold overflow-hidden">
                                    {parentUser?.photo_url ? <img src={parentUser.photo_url} alt="" className="w-full h-full object-cover" /> : (parentUser?.first_name || parentUser?.name || "U").slice(0, 1).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{parentUser?.first_name || parentUser?.name || "Myself"}</div>
                                    <div className="text-[11px] text-gray-500">Account owner</div>
                                </div>
                                {!activeDependentId && <Check className="w-4 h-4 text-emerald-600" />}
                            </button>
                        </li>
                        {approved.map((d) => {
                            const Icon = relIcon(d.relationship);
                            return (
                                <li key={d.id}>
                                    <button
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-orange-50 ${activeDependentId === d.id ? "bg-orange-50/40" : ""}`}
                                        onClick={() => { onChange(d.id); setOpen(false); }}
                                        data-testid={`profile-switcher-dep-${d.id}`}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white">
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 truncate">{d.name}</div>
                                            <div className="text-[11px] text-gray-500 capitalize">{d.relationship} · age {d.age}</div>
                                        </div>
                                        {activeDependentId === d.id && <Check className="w-4 h-4 text-emerald-600" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100">
                        <a href="#family" onClick={() => setOpen(false)} className="text-xs text-[var(--medx-red)] font-semibold inline-flex items-center gap-1" data-testid="profile-switcher-manage">
                            <UsersIcon className="w-3.5 h-3.5" /> Manage family accounts
                        </a>
                    </div>
                </div>
            )}
            {activeDep && (
                <div className="absolute -bottom-1 right-2 text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white" style={{ transform: "translateY(100%)" }}>
                    DEPENDENT ACTIVE
                </div>
            )}
        </div>
    );
}
